// Zoom and pan maths of the image viewer (file-lightbox.tsx), kept apart from
// the input (fingers, trackpad, wheel) as pure functions.
//
// The image sits in the middle of the screen and is moved with
// `transform: translate(x, y) scale(s)` (origin at the image's center). `x` and
// `y` are screen pixels.

export interface Zoom {
  s: number;
  x: number;
  y: number;
}

export const ZOOM_IDENTITY: Zoom = { s: 1, x: 0, y: 0 };

/** Upper bound of the zoom. Enough to read the details of a full-size image. */
export const ZOOM_MAX = 8;

/** The zoom a double tap / double click goes to. */
export const ZOOM_STEP = 2.5;

/** A size in screen pixels (the image before zooming, or the viewer). */
export interface Box {
  width: number;
  height: number;
}

/** How far the picture may move off center (0 = centered on an axis where it is smaller than the screen). */
function bounds(s: number, box: Box, view: Box): { bx: number; by: number } {
  return {
    bx: Math.max(0, (box.width * s - view.width) / 2),
    by: Math.max(0, (box.height * s - view.height) / 2),
  };
}

/**
 * Keeps the zoomed picture's edges from coming inside the screen's edges
 * (centered on an axis where it is smaller). Being able to drag into the dark
 * around the picture only adds work to bring it back. At `s <= 1` it goes back
 * to the center. `view` defaults to the image's own box.
 */
export function clampZoom(z: Zoom, box: Box, view: Box = box): Zoom {
  if (z.s <= 1) return ZOOM_IDENTITY;
  const { bx, by } = bounds(z.s, box, view);
  return {
    s: z.s,
    x: Math.min(bx, Math.max(-bx, z.x)),
    y: Math.min(by, Math.max(-by, z.y)),
  };
}

/**
 * Changes the zoom to `next` while the point of the picture under `at`
 * (measured from the image's center) stays put. Not clamped: the raw value
 * while a finger is moving.
 */
export function zoomAtFree(
  z: Zoom,
  next: number,
  at: { x: number; y: number },
): Zoom {
  const qx = (at.x - z.x) / z.s;
  const qy = (at.y - z.y) / z.s;
  return { s: next, x: at.x - qx * next, y: at.y - qy * next };
}

/**
 * {@link zoomAtFree} with the zoom kept to 1..{@link ZOOM_MAX} and the
 * overflow clamped — zooming around the fingers / the pointer.
 */
export function zoomAt(
  z: Zoom,
  next: number,
  at: { x: number; y: number },
  box: Box,
  view: Box = box,
): Zoom {
  const s = Math.min(ZOOM_MAX, Math.max(1, next));
  return clampZoom(zoomAtFree(z, s, at), box, view);
}

/**
 * How far it actually moves when pulled `over` past the range: heavier the
 * further you pull, never beyond `limit` (the same curve as iOS scroll edges).
 */
export function rubber(over: number, limit: number): number {
  if (over <= 0 || limit <= 0) return 0;
  return (1 - 1 / ((over * 0.55) / limit + 1)) * limit;
}

/** Outside [lo, hi], shrinks the excess with {@link rubber}. */
function resist(v: number, lo: number, hi: number, limit: number): number {
  if (v < lo) return lo - rubber(lo - v, limit);
  if (v > hi) return hi + rubber(v - hi, limit);
  return v;
}

/**
 * The position to *show* while fingers move. It may go out of range (smaller
 * than fit, larger than the maximum, past the edges) but gets heavier; on
 * release it goes back with {@link settleZoom}. Stopping dead at the edge
 * did not tell the finger how far it could go, and felt not smooth (user
 * feedback).
 */
export function rubberZoom(raw: Zoom, box: Box, view: Box = box): Zoom {
  const s = resist(raw.s, 1, ZOOM_MAX, 0.5);
  const { bx, by } = bounds(s, box, view);
  // Even when shrinking pulled the picture off center, the pull-back is at most half the screen.
  return {
    s,
    x: resist(raw.x, -bx, bx, view.width / 2),
    y: resist(raw.y, -by, by, view.height / 2),
  };
}

/** Where it settles after the fingers lift (zoom in 1..max, position in range). */
export function settleZoom(z: Zoom, box: Box, view: Box = box): Zoom {
  if (z.s <= 1) return ZOOM_IDENTITY;
  if (z.s > ZOOM_MAX) return zoomAt(z, ZOOM_MAX, { x: 0, y: 0 }, box, view);
  return clampZoom(z, box, view);
}

/**
 * How long (ms) a release at some speed (px/ms) keeps gliding: it lands at
 * speed × this. Close to, a little shorter than, where iOS's default
 * deceleration (0.998 per frame) stops.
 */
export const FLING_MS = 300;

/** Where a flick lands (in range). */
export function flingZoom(
  z: Zoom,
  v: { x: number; y: number },
  box: Box,
  view: Box = box,
): Zoom {
  return settleZoom(
    { s: z.s, x: z.x + v.x * FLING_MS, y: z.y + v.y * FLING_MS },
    box,
    view,
  );
}

/** Between two positions (`t` in 0..1), each of zoom and offset linearly. */
export function mixZoom(a: Zoom, b: Zoom, t: number): Zoom {
  return {
    s: a.s + (b.s - a.s) * t,
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

/** A motion that slows down to a stop (ease-out cubic). */
export function easeOut(t: number): number {
  const u = 1 - Math.min(1, Math.max(0, t));
  return 1 - u * u * u;
}
